import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthenticatedClient, queryKeys } from '@/api/queries'
import type { components } from '@/api/schema'

export type PhoneCall = components['schemas']['PhoneCallRead']
export type PhoneCallListItem = components['schemas']['PhoneCallListItemRead']
export type PhoneCallFileOption = components['schemas']['PhoneCallFileOption']

const phoneCallKeys = {
  all: ['phone-calls'] as const,
  byMessage: (messageUuid: string, mailbox: 'supplier' | 'customer') =>
    ['phone-calls', 'by-message', mailbox, messageUuid] as const,
  list: (params: PhoneCallListParams) => ['phone-calls', 'list', params] as const,
}

export type PhoneCallListParams =
  | { manufacturer_uuid: string }
  | { supplier_id: number }
  | { customer_group_id: number }

/** The Quo call a conversation's call message was filed from (USA-217). */
export function usePhoneCallForMessage(messageUuid: string | undefined, mailbox: 'supplier' | 'customer') {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: phoneCallKeys.byMessage(messageUuid ?? '', mailbox),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/phone-calls/by-message/{message_uuid}', {
        params: { path: { message_uuid: messageUuid! }, query: { mailbox } },
      })
      if (error) throw error
      return data
    },
    enabled: !!messageUuid,
    staleTime: 5 * 60_000,
  })
}

/** A short-lived link to the recording, fetched when someone presses play. */
export function useCallRecording() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async ({ callUuid, voicemail }: { callUuid: string; voicemail?: boolean }) => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/phone-calls/{call_uuid}/recording', {
        params: { path: { call_uuid: callUuid }, query: { voicemail: !!voicemail } },
      })
      if (error) throw error
      return data
    },
  })
}

/** A supplier's or customer's calls, newest first. */
export function usePhoneCalls(params: PhoneCallListParams | null, search = '') {
  const getClient = useAuthenticatedClient()
  const q = search.trim()
  return useQuery({
    queryKey: [...phoneCallKeys.list(params ?? { supplier_id: 0 }), q] as const,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/phone-calls', {
        params: { query: { ...params!, ...(q ? { q } : {}) } },
      })
      if (error) throw error
      return data
    },
    enabled: !!params,
    placeholderData: (previous) => previous,
  })
}

/** File a call on the record it was about; it then appears in that record's conversation. */
export function useFileCall() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ callUuid, option }: { callUuid: string; option: PhoneCallFileOption }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/phone-calls/{call_uuid}/file', {
        params: { path: { call_uuid: callUuid } },
        body: { kind: option.kind, id: option.id },
      })
      if (error) throw error
      return data
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: phoneCallKeys.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}

/** The conversation a call is picked from: its PO, invoice, or customer conversation. */
export type CallTarget =
  | { purchase_order_id: number }
  | { ap_invoice_id: number }
  | { customer_thread_id: number }

/** Recent Quo calls to link from Record Communication, this caller's first (USA-217). */
export function useRecentCalls(target: CallTarget | null, enabled: boolean, search = '') {
  const getClient = useAuthenticatedClient()
  const q = search.trim()
  return useQuery({
    queryKey: [...phoneCallKeys.all, 'recent', target, q] as const,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/phone-calls/recent', {
        params: { query: { ...target!, ...(q ? { q } : {}) } },
      })
      if (error) throw error
      return data
    },
    enabled: enabled && !!target,
    placeholderData: (previous) => previous,
  })
}

/** Put a call on this conversation's record; it leaves wherever it was filed before. */
export function useLinkCall() {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ callUuid, target }: { callUuid: string; target: CallTarget }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/phone-calls/{call_uuid}/link', {
        params: { path: { call_uuid: callUuid } },
        body: target,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => toast.success('Call linked'),
    onError: () => toast.error('Could not link the call'),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: phoneCallKeys.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.communications.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.customerComms.all })
    },
  })
}
